import { put, del, list, head } from '@vercel/blob';

class VercelBlobService {
  constructor() {
    this.baseUrl = 'https://gifted-solutions-ecommerce.vercel.app';
    this.bucketName = 'products';
  }

  /**
   * Upload a file to Vercel Blob storage
   * @param {File} file - The file to upload
   * @param {string} filename - The desired filename
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadFile(file, filename, options = {}) {
    try {
      const blob = await put(filename, file, {
        access: 'public',
        addRandomSuffix: false,
        ...options
      });

      return {
        success: true,
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt
      };
    } catch (error) {
      console.error('Error uploading file to Vercel Blob:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Upload multiple files
   * @param {Array} files - Array of {file, filename} objects
   * @returns {Promise<Array>} Array of upload results
   */
  async uploadMultipleFiles(files) {
    const uploadPromises = files.map(({ file, filename, options }) =>
      this.uploadFile(file, filename, options)
    );

    try {
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      throw error;
    }
  }

  /**
   * Delete a file from Vercel Blob storage
   * @param {string} url - The blob URL to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFile(url) {
    try {
      await del(url);
      return {
        success: true,
        message: 'File deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting file from Vercel Blob:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * List all files in the blob storage
   * @param {Object} options - List options
   * @returns {Promise<Array>} Array of blob objects
   */
  async listFiles(options = {}) {
    try {
      const { blobs } = await list({
        prefix: options.prefix || '',
        limit: options.limit || 1000,
        cursor: options.cursor
      });

      return {
        success: true,
        files: blobs.map(blob => ({
          url: blob.url,
          downloadUrl: blob.downloadUrl,
          pathname: blob.pathname,
          size: blob.size,
          uploadedAt: blob.uploadedAt
        })),
        hasMore: blobs.length === (options.limit || 1000)
      };
    } catch (error) {
      console.error('Error listing files from Vercel Blob:', error);
      return {
        success: false,
        error: error.message,
        files: []
      };
    }
  }

  /**
   * Get file metadata
   * @param {string} url - The blob URL
   * @returns {Promise<Object>} File metadata
   */
  async getFileMetadata(url) {
    try {
      const metadata = await head(url);
      return {
        success: true,
        metadata: {
          url: metadata.url,
          downloadUrl: metadata.downloadUrl,
          pathname: metadata.pathname,
          size: metadata.size,
          uploadedAt: metadata.uploadedAt,
          contentType: metadata.contentType,
          cacheControl: metadata.cacheControl
        }
      };
    } catch (error) {
      console.error('Error getting file metadata:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate a public URL for a file
   * @param {string} filename - The filename
   * @returns {string} Public URL
   */
  generatePublicUrl(filename) {
    // For Vercel Blob, the URL is returned from the upload
    // This is a helper for constructing URLs if needed
    return `${this.baseUrl}/_vercel/blob/${filename}`;
  }

  /**
   * Check if a file exists
   * @param {string} url - The blob URL
   * @returns {Promise<boolean>} Whether the file exists
   */
  async fileExists(url) {
    try {
      const result = await this.getFileMetadata(url);
      return result.success;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all product images
   * @returns {Promise<Array>} Array of product image URLs
   */
  async getProductImages() {
    try {
      const result = await this.listFiles({ prefix: 'products/' });
      
      if (result.success) {
        return result.files.filter(file => {
          const pathname = file.pathname.toLowerCase();
          return pathname.endsWith('.jpg') || 
                 pathname.endsWith('.jpeg') || 
                 pathname.endsWith('.png') || 
                 pathname.endsWith('.webp') || 
                 pathname.endsWith('.gif');
        });
      }
      
      return [];
    } catch (error) {
      console.error('Error getting product images:', error);
      return [];
    }
  }

  /**
   * Upload product image with automatic naming
   * @param {File} file - The image file
   * @param {string} productName - Product name for filename
   * @returns {Promise<Object>} Upload result
   */
  async uploadProductImage(file, productName) {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const sanitizedName = productName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const filename = `products/${sanitizedName}.${fileExtension}`;
    
    return await this.uploadFile(file, filename, {
      contentType: file.type
    });
  }

  /**
   * Migrate from Supabase to Vercel Blob
   * @param {Array} supabaseImages - Array of Supabase image objects
   * @returns {Promise<Array>} Migration results
   */
  async migrateFromSupabase(supabaseImages) {
    const migrationResults = [];
    
    for (const image of supabaseImages) {
      try {
        // Download image from Supabase
        const response = await fetch(image.url);
        const blob = await response.blob();
        
        // Create file object
        const file = new File([blob], image.name, { type: blob.type });
        
        // Upload to Vercel Blob
        const result = await this.uploadProductImage(file, image.name);
        
        migrationResults.push({
          originalUrl: image.url,
          newUrl: result.url,
          success: result.success,
          filename: image.name
        });
        
        console.log(`Migrated: ${image.name} -> ${result.success ? 'Success' : 'Failed'}`);
      } catch (error) {
        console.error(`Failed to migrate ${image.name}:`, error);
        migrationResults.push({
          originalUrl: image.url,
          success: false,
          error: error.message,
          filename: image.name
        });
      }
    }
    
    return migrationResults;
  }
}

// Create and export singleton instance
const vercelBlobService = new VercelBlobService();
export default vercelBlobService;
